import { memo, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Coins, Globe, FileText, Loader2, ChevronRight, Lightbulb, ArrowRight, Check, Copy, MessageCircleQuestion, ExternalLink, Asterisk } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { ChatMessage as ChatMessageType, StructuredContent, StructuredSection, StructuredReplacement, StructuredNumberedItem } from '@/api/chat';

interface ChatMessageProps {
  message: ChatMessageType;
  isLoading?: boolean;
  onOpenDraft?: (content: string) => void;
  compact?: boolean;
}

// Parse structured content from AI response
function parseStructuredContent(content: string): {
  sections: Array<{ type: 'text' | 'heading' | 'list' | 'numbered-list' | 'tip'; content: string; items?: string[] }>;
  hasDraft: boolean;
  draftContent: string | null;
} {
  // Extract draft content
  const draftMatch = content.match(/<draft>([\s\S]*?)<\/draft>/);
  const hasDraft = !!draftMatch;
  const draftContent = draftMatch ? draftMatch[1].trim() : null;

  // Remove draft tags for display
  let displayContent = content.replace(/<draft>[\s\S]*?<\/draft>/g, '').trim();

  const sections: Array<{ type: 'text' | 'heading' | 'list' | 'numbered-list' | 'tip'; content: string; items?: string[] }> = [];

  // Split by lines first to handle mixed content better
  const lines = displayContent.split('\n');
  let currentSection: { type: 'text' | 'heading' | 'list' | 'numbered-list' | 'tip'; content: string; items?: string[] } | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      // Empty line - flush current section
      if (currentSection) {
        sections.push(currentSection);
        currentSection = null;
      }
      continue;
    }

    // Check for markdown headings (## or ###)
    if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) {
      if (currentSection) {
        sections.push(currentSection);
      }
      sections.push({ type: 'heading', content: trimmed.replace(/^#{2,3}\s*/, '') });
      currentSection = null;
    }
    // Check for horizontal rule (---)
    else if (trimmed === '---') {
      if (currentSection) {
        sections.push(currentSection);
        currentSection = null;
      }
      // Skip the rule itself
    }
    // Check for numbered list items (1. 2. 3. etc)
    else if (/^\d+\.\s/.test(trimmed)) {
      const item = trimmed.replace(/^\d+\.\s*/, '').trim();
      if (currentSection?.type === 'numbered-list') {
        currentSection.items?.push(item);
      } else {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = { type: 'numbered-list', content: '', items: [item] };
      }
    }
    // Check for bullet list items (- or •)
    else if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      const item = trimmed.replace(/^[-•]\s*/, '').trim();
      if (currentSection?.type === 'list') {
        currentSection.items?.push(item);
      } else {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = { type: 'list', content: '', items: [item] };
      }
    }
    // Check for tips/notes
    else if (trimmed.toLowerCase().startsWith('tip:') || trimmed.toLowerCase().startsWith('note:')) {
      if (currentSection) {
        sections.push(currentSection);
      }
      sections.push({ type: 'tip', content: trimmed.replace(/^(tip|note):\s*/i, '') });
      currentSection = null;
    }
    // Regular text - append to current text section or create new one
    else {
      if (currentSection?.type === 'text') {
        currentSection.content += '\n' + trimmed;
      } else {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = { type: 'text', content: trimmed };
      }
    }
  }

  // Flush any remaining section
  if (currentSection) {
    sections.push(currentSection);
  }

  return { sections, hasDraft, draftContent };
}

// Parse inline markdown (bold, italic, code, links) and render as React elements
function renderTextWithLinks(text: string): React.ReactNode {
  // Combined regex for markdown patterns
  // Order matters: bold before italic to avoid conflicts with **
  const patterns = [
    { regex: /\*\*([^*]+)\*\*/g, render: (content: string, key: string) => <strong key={key} className="font-semibold">{content}</strong> },
    { regex: /\*([^*]+)\*/g, render: (content: string, key: string) => <em key={key}>{content}</em> },
    { regex: /`([^`]+)`/g, render: (content: string, key: string) => <code key={key} className="px-1 py-0.5 bg-muted rounded text-sm font-mono">{content}</code> },
    {
      regex: /\[([^\]]+)\]\(([^)]+)\)/g, render: (content: string, key: string, url?: string) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-0.5"
        >
          {content}
          <ExternalLink className="h-3 w-3 inline" />
        </a>
      )
    },
  ];

  // Process text iteratively to handle nested/mixed patterns
  let result: React.ReactNode[] = [text];
  let keyCounter = 0;

  for (const { regex, render } of patterns) {
    const newResult: React.ReactNode[] = [];

    for (const part of result) {
      if (typeof part !== 'string') {
        newResult.push(part);
        continue;
      }

      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      // Reset regex lastIndex for each part
      regex.lastIndex = 0;

      while ((match = regex.exec(part)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(part.slice(lastIndex, match.index));
        }

        // Handle link pattern (has URL in match[2])
        if (match[2]) {
          parts.push(render(match[1], `md-${keyCounter++}`, match[2]));
        } else {
          parts.push(render(match[1], `md-${keyCounter++}`));
        }

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < part.length) {
        parts.push(part.slice(lastIndex));
      }

      newResult.push(...(parts.length > 0 ? parts : [part]));
    }

    result = newResult;
  }

  return result.length === 1 && typeof result[0] === 'string' ? result[0] : result;
}

// Type guards for structured content items
function isReplacement(item: any): item is StructuredReplacement {
  return typeof item === 'object' && 'original' in item && 'replacement' in item;
}

function isNumberedItem(item: any): item is StructuredNumberedItem {
  return typeof item === 'object' && 'heading' in item && 'content' in item;
}

// Render structured feedback content
function StructuredFeedback({
  content,
  onCopyReplacement
}: {
  content: StructuredContent;
  onCopyReplacement?: (text: string) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Summary */}
      {content.summary && (
        <p className="text-foreground font-medium">{renderTextWithLinks(content.summary)}</p>
      )}

      {/* Sections */}
      {content.sections.map((section, idx) => (
        <div key={idx} className="space-y-2">
          <h4 className="font-semibold text-foreground text-sm">{section.title}</h4>

          {/* List type */}
          {section.type === 'list' && (
            <ul className="space-y-1.5">
              {section.items.map((item, i) => (
                <li key={i} className="flex gap-2 items-start text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/90">{typeof item === 'string' ? renderTextWithLinks(item) : ''}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Numbered type */}
          {section.type === 'numbered' && (
            <ol className="space-y-3">
              {section.items.map((item, i) => {
                if (isNumberedItem(item)) {
                  return (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-sm text-foreground">{renderTextWithLinks(item.heading)}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{renderTextWithLinks(item.content)}</p>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                      {i + 1}
                    </span>
                    <span className="text-sm text-foreground/90">{String(item)}</span>
                  </li>
                );
              })}
            </ol>
          )}

          {/* Replacements type - clickable suggestions */}
          {section.type === 'replacements' && (
            <div className="space-y-2">
              {section.items.map((item, i) => {
                if (isReplacement(item)) {
                  return (
                    <button
                      key={i}
                      onClick={() => onCopyReplacement?.(item.replacement)}
                      className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors group"
                    >
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <span className="line-through">{item.original}</span>
                        <ArrowRight className="h-3 w-3" />
                        <span className="text-primary font-medium">{item.replacement}</span>
                        <Copy className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {item.reason && (
                        <p className="text-xs text-muted-foreground">{item.reason}</p>
                      )}
                    </button>
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Text type */}
          {section.type === 'text' && section.content && (
            <p className="text-sm text-foreground/90 leading-relaxed">{section.content}</p>
          )}
        </div>
      ))}

      {/* Questions */}
      {content.questions && content.questions.length > 0 && (
        <div className="pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <MessageCircleQuestion className="h-4 w-4" />
            <span className="font-medium">Questions to refine further:</span>
          </div>
          <ul className="space-y-1">
            {content.questions.map((q, i) => (
              <li key={i} className="text-sm text-foreground/80 pl-6">• {q}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const ChatMessage = memo(function ChatMessage({
  message,
  isLoading,
  onOpenDraft,
  compact
}: ChatMessageProps) {
  const { toast } = useToast();
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Always parse markdown content for analysis results (preferred layout)
  // Only use structured_content for non-analysis types (e.g., drafts, general chat)
  const structuredContent = message.structured_content?.type !== 'analysis' ? message.structured_content : null;
  const parsed = isAssistant && !structuredContent ? parseStructuredContent(message.content) : null;
  const hasDraft = parsed?.hasDraft || message.has_draft;
  const draftContent = parsed?.draftContent || message.draft_content;

  // Handler for copying replacement text
  const handleCopyReplacement = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      description: 'Paste the suggestion into your content.',
    });
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        isUser ? 'items-end' : 'items-start'
      )}
    >
      {/* User message bubble */}
      {isUser && (
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 rounded-br-md',
            'bg-primary text-primary-foreground',
            compact ? 'max-w-full text-sm' : 'max-w-[85%]'
          )}
        >
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      )}

      {/* Assistant message - structured rendering */}
      {isAssistant && (
        <div className={cn('w-full', compact ? 'text-sm' : '')}>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-2">
              <Asterisk className="h-4 w-4 animate-spin" />
              <span>Thinking...</span>
            </div>
          ) : structuredContent ? (
            // Render rich structured content from API
            <StructuredFeedback
              content={structuredContent}
              onCopyReplacement={handleCopyReplacement}
            />
          ) : parsed && parsed.sections.length > 0 ? (
            // Fallback to legacy markdown parsing
            <div className="space-y-3">
              {parsed.sections.map((section, idx) => (
                <div key={idx}>
                  {section.type === 'heading' && (
                    <h4 className="font-semibold text-foreground">{section.content}</h4>
                  )}
                  {section.type === 'text' && (
                    <p className="text-foreground/90 leading-relaxed">{renderTextWithLinks(section.content)}</p>
                  )}
                  {section.type === 'numbered-list' && section.items && (
                    <ol className="space-y-2 ml-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                            {i + 1}
                          </span>
                          <span className="text-foreground/90">{renderTextWithLinks(item)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                  {section.type === 'list' && section.items && (
                    <ul className="space-y-1.5 ml-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex gap-2 items-start">
                          <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-foreground/90">{renderTextWithLinks(item)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.type === 'tip' && (
                    <div className="flex gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                      <Lightbulb className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground/80">{renderTextWithLinks(section.content)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-foreground/90 whitespace-pre-wrap">
              {renderTextWithLinks(message.content.replace(/<draft>[\s\S]*?<\/draft>/g, '').replace(/<structured>[\s\S]*?<\/structured>/g, '').trim()) || 'Working on your request...'}
            </p>
          )}
        </div>
      )}

      {/* Draft indicator - draft is auto-saved as content piece */}
      {isAssistant && hasDraft && draftContent && (
        <div className={cn(
          "flex items-center gap-2 p-2 rounded-lg border border-primary/20 bg-primary/5",
          compact ? "mt-1" : "mt-2"
        )}>
          <Check className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm text-primary">Draft saved to editor</span>
        </div>
      )}

      {/* Metadata row */}
      {isAssistant && (message.used_web_search || (message.credits_consumed && message.credits_consumed > 0)) && (
        <div className={cn(
          "flex items-center gap-3 text-xs text-muted-foreground",
          compact ? "mt-0.5" : "mt-1"
        )}>
          {message.used_web_search && (
            <div className="flex items-center gap-1">
              <Globe className="h-3 w-3" />
              <span>Web search</span>
            </div>
          )}
          {message.credits_consumed && message.credits_consumed > 0 && (
            <div className="flex items-center gap-1">
              <Coins className="h-3 w-3" />
              <span>{message.credits_consumed} credit{message.credits_consumed !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Thinking messages that cycle while AI is generating
const THINKING_MESSAGES = [
  "Thinking...",
  "Crafting your response...",
  "Analyzing your request...",
  "Finding the right words...",
  "Putting ideas together...",
  "Almost there...",
];

// Typing indicator component with cycling messages
export function TypingIndicator() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % THINKING_MESSAGES.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-start">
      <div className="rounded-2xl rounded-bl-md py-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground animate-pulse">
            {THINKING_MESSAGES[messageIndex]}
          </span>
        </div>
      </div>
    </div>
  );
}
